import React, { useState, useEffect } from 'react';
import {
  X,
  ClipboardList,
  CreditCard,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import styles from './AddVendorPaymentDrawer.module.css';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';

const AddVendorPaymentDrawer = ({ isOpen, onClose, onSuccess, payment }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [filteredPOs, setFilteredPOs] = useState([]);

  const [formData, setFormData] = useState({
    project_id: '',
    vendor_id: '',
    purchase_order_id: '',
    status: 'partial',
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_mode: '',
    bank_account_id: '',
    ref_transaction_number: '',
    remarks: ''
  });

  const [selectedPO, setSelectedPO] = useState(null);
  const [poSummary, setPoSummary] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  useEffect(() => {
    const initDrawer = async () => {
      if (isOpen) {
        setIsFetching(true);
        try {
          await fetchDropdowns();
          if (payment) {
            await fetchRecord(payment.id);
          } else {
            setFormData({
              project_id: '',
              vendor_id: '',
              purchase_order_id: '',
              status: 'partial',
              payment_date: new Date().toISOString().split('T')[0],
              amount: '',
              payment_mode: '',
              bank_account_id: '',
              ref_transaction_number: '',
              remarks: ''
            });
            setSelectedPO(null);
            setPoSummary(null);
          }
        } finally {
          setIsFetching(false);
        }
      }
    };
    initDrawer();
  }, [isOpen, payment]);

  const fetchRecord = async (id) => {
    try {
      const response = await api.get(`/vendor-payments/${id}`);
      if (response.data.success) {
        const p = response.data.data;
        setFormData({
          project_id: p.project_id || '',
          vendor_id: p.vendor_id || '',
          purchase_order_id: p.purchase_order_id || '',
          status: p.status || 'partial',
          payment_date: p.payment_date || new Date().toISOString().split('T')[0],
          amount: p.amount || '',
          payment_mode: p.payment_mode ? String(p.payment_mode) : '',
          bank_account_id: p.bank_account_id || '',
          ref_transaction_number: p.ref_transaction_number || '',
          remarks: p.remarks || ''
        });
        if (p.purchase_order) {
          setSelectedPO(p.purchase_order);
          await fetchPoSummary(p.purchase_order_id, id);
        }
      }
    } catch (error) {
      console.error('Error fetching record:', error);
      toast.error('Failed to load payment details');
    }
  };

  const fetchDropdowns = async () => {
    setIsLoading(true);
    try {
      const [projRes, vendRes, bankRes, poRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/vendors-dropdown'),
        api.get('/bank-accounts-dropdown'),
        api.get('/purchase-orders')
      ]);

      if (projRes.data.success) setProjects(projRes.data.data);
      if (vendRes.data.success) setVendors(vendRes.data.data);
      if (bankRes.data.success) setBankAccounts(bankRes.data.data);
      if (poRes.data.success) setPurchaseOrders(poRes.data.data.data);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
      toast.error('Failed to load form data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (formData.project_id && formData.vendor_id) {
      const filtered = purchaseOrders.filter(po =>
        po.project_id === formData.project_id && po.vendor_id === formData.vendor_id
      );
      setFilteredPOs(filtered);
    } else {
      setFilteredPOs([]);
    }
  }, [formData.project_id, formData.vendor_id, purchaseOrders]);

  const fetchPoSummary = async (poId, excludeId = null) => {
    if (!poId) {
      setPoSummary(null);
      return;
    }
    setIsSummaryLoading(true);
    try {
      const [summaryRes, advanceRes] = await Promise.all([
        api.post('/vendor-payments/po-payment-summary', { purchase_order_id: poId }),
        excludeId ? api.post('/vendor-payments/po-advance', {
          purchase_order_id: poId,
          exclude_payment_id: excludeId
        }) : Promise.resolve(null)
      ]);

      if (summaryRes.data.success) {
        const data = summaryRes.data.data;
        if (advanceRes && advanceRes.data.success) {
          data.total_payments_made_others = advanceRes.data.data.total_payment;
          data.remaining_balance_others = data.po_total_amount - advanceRes.data.data.total_payment;
        }
        setPoSummary(data);
      }
    } catch (error) {
      console.error('Error fetching PO summary:', error);
    } finally {
      setIsSummaryLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'purchase_order_id') {
      const po = purchaseOrders.find(p => p.id === value);
      setSelectedPO(po);
      fetchPoSummary(value, payment?.id);
    }

    if (name === 'project_id' || name === 'vendor_id') {
      setPoSummary(null);
      setSelectedPO(null);
      setFormData(prev => ({ ...prev, purchase_order_id: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      let response;
      if (payment) {
        response = await api.put(`/vendor-payments/${payment.id}`, formData);
      } else {
        response = await api.post('/vendor-payments', formData);
      }

      if (response.data.success) {
        toast.success(response.data.message || (payment ? 'Payment updated successfully!' : 'Payment recorded successfully!'));
        if (onSuccess) onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error saving vendor payment:', error);
      toast.error(error.response?.data?.message || 'Failed to save payment');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`${styles['drawer-overlay']} ${isOpen ? styles['open'] : ''}`} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={(e) => e.stopPropagation()}>
        <header className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{payment ? 'Edit Vendor Payment' : 'Add Vendor Payment'}</h2>
            <p>{payment ? 'Modify existing payment record' : 'Record a new payment for a vendor'}</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </header>

        <div className={styles['drawer-body']}>
          {isFetching ? (
            <div className={styles['loading-container']}>
              <div className={styles['spinner-wrapper']}>
                <Loader2 size={44} className={styles['spinner']} />
              </div>
              <span className={styles['loading-text']}>Fetching technical details...</span>
            </div>
          ) : (
            <>
              {/* Section 1: PO & Vendor Selection */}
              <div className={styles['drawer-section']}>
                <h3 className={styles['section-title-vendorpayment']}>
                  <ClipboardList size={18} className={styles['section-icon']} />
                  PO & Vendor Selection
                </h3>
                <div className={styles['form-grid-vendorpayment']}>
                  <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                    <label>Project</label>
                    <CustomSelect
                      placeholder="Select Project"
                      options={projects.map(p => ({ value: p.id, label: p.project_name }))}
                      value={formData.project_id ? { value: formData.project_id, label: projects.find(p => p.id === formData.project_id)?.project_name } : null}
                      onChange={(opt) => handleInputChange('project_id', opt ? opt.value : '')}
                      isLoading={isLoading}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Vendor</label>
                    <CustomSelect
                      placeholder="Select Vendor"
                      options={vendors.map(v => ({ value: v.id, label: v.vendor_name }))}
                      value={formData.vendor_id ? { value: formData.vendor_id, label: vendors.find(v => v.id === formData.vendor_id)?.vendor_name } : null}
                      onChange={(opt) => handleInputChange('vendor_id', opt ? opt.value : '')}
                      isLoading={isLoading}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>PO#</label>
                    <CustomSelect
                      placeholder="Select PO"
                      options={filteredPOs.map(po => ({ value: po.id, label: po.po_number }))}
                      value={formData.purchase_order_id ? { value: formData.purchase_order_id, label: filteredPOs.find(p => p.id === formData.purchase_order_id)?.po_number } : null}
                      onChange={(opt) => handleInputChange('purchase_order_id', opt ? opt.value : '')}
                      isDisabled={!formData.project_id || !formData.vendor_id}
                    />
                  </div>
                  <div className={[styles['form-group'], styles['full-width']].join(' ')}>
                    <label>Remarks</label>
                    <input
                      type="text"
                      placeholder="Any internal remarks..."
                      value={formData.remarks}
                      onChange={(e) => handleInputChange('remarks', e.target.value)}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Payment Date</label>
                    <CustomDatePicker
                      selected={formData.payment_date}
                      onChange={(date) => handleInputChange('payment_date', date ? date.toISOString().split('T')[0] : '')}
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label>Payment Status</label>
                    <CustomSelect
                      placeholder="Select Status"
                      options={[
                        { value: 'advance', label: 'Advance' },
                        { value: 'partial', label: 'Partial' },
                        { value: 'final', label: 'Final' }
                      ]}
                      value={[
                        { value: 'advance', label: 'Advance' },
                        { value: 'partial', label: 'Partial' },
                        { value: 'final', label: 'Final' }
                      ].find(opt => opt.value === formData.status)}
                      onChange={(opt) => handleInputChange('status', opt ? opt.value : 'partial')}
                    />
                  </div>
                </div>

                {(isSummaryLoading || poSummary) && (
                  <div className={styles['payment-summary-box-vendorpayment']}>
                    {isSummaryLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9a6e3a', fontWeight: 600, padding: '4px 0' }}>
                        <Loader2 size={16} className={styles['spinner']} />
                        <span style={{ fontSize: '0.85rem' }}>Updating payment summary...</span>
                      </div>
                    ) : (
                      <>
                        <div className={styles['summary-item']}>
                          <span className={styles['summary-label']}>Total PO</span>
                          <span className={styles['summary-value']}>₹{parseFloat(poSummary.po_total_amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                        <div className={styles['summary-item']}>
                          <span className={styles['summary-label']}>{payment ? 'Other Paid' : 'Paid So Far'}</span>
                          <span className={[styles['summary-value'], styles['success']].join(' ')}>
                            ₹{parseFloat(payment ? (poSummary.total_payments_made_others || 0) : (poSummary.total_payments_made || 0)).toLocaleString('en-IN')}
                          </span>
                        </div>
                        <div className={styles['summary-item']}>
                          <span className={styles['summary-label']}>{payment ? 'Available' : 'Remaining'}</span>
                          <span
                            className={[styles['summary-value'], styles['highlight'], styles['clickable']].join(' ')}
                            onClick={() => {
                              const remaining = payment ? (poSummary.remaining_balance_others || 0) : (poSummary.remaining_balance || 0);
                              handleInputChange('amount', remaining);
                            }}
                            title="Click to pay full remaining amount"
                          >
                            ₹{parseFloat(payment ? (poSummary.remaining_balance_others || 0) : (poSummary.remaining_balance || 0)).toLocaleString('en-IN')}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Section 2: Payment Details */}
              <div className={styles['drawer-section']}>
                <h3 className={styles['section-title-vendorpayment']}>
                  <CreditCard size={18} className={styles['section-icon']} />
                  Payment Details
                </h3>
                <div className={styles['form-grid-vendorpayment']}>
                  <div className={styles['form-group']}>
                    <label>Payment Mode</label>
                    <CustomSelect
                      placeholder="Select Mode"
                      options={[
                        { value: '1', label: 'Bank Transfer' },
                        { value: '2', label: 'Cheque' },
                        { value: '3', label: 'Cash' },
                        { value: '4', label: 'Other (NEFT/RTGS)' }
                      ]}
                      value={[
                        { value: '1', label: 'Bank Transfer' },
                        { value: '2', label: 'Cheque' },
                        { value: '3', label: 'Cash' },
                        { value: '4', label: 'Other (NEFT/RTGS)' }
                      ].find(opt => opt.value === formData.payment_mode)}
                      onChange={(opt) => handleInputChange('payment_mode', opt ? opt.value : '')}
                    />
                  </div>
                  {formData.payment_mode !== '3' && (
                    <>
                      <div className={styles['form-group']}>
                        <label>Ref / Transaction#</label>
                        <input
                          type="text"
                          placeholder="e.g. TXN-9821"
                          value={formData.ref_transaction_number}
                          onChange={(e) => handleInputChange('ref_transaction_number', e.target.value)}
                        />
                      </div>
                      <div className={styles['form-group']}>
                        <label>Bank Account</label>
                        <CustomSelect
                          placeholder="Select Account"
                          options={bankAccounts.map(b => ({ value: b.id, label: `${b.bank_name}` }))}
                          value={formData.bank_account_id ? { value: formData.bank_account_id, label: bankAccounts.find(b => b.id === formData.bank_account_id)?.bank_name } : null}
                          onChange={(opt) => handleInputChange('bank_account_id', opt ? opt.value : '')}
                          isLoading={isLoading}
                        />
                      </div>
                    </>
                  )}
                  <div className={styles['form-group']}>
                    <label>Amount Paying</label>
                    <input
                      type="number"
                      placeholder="0.00"
                      value={formData.amount}
                      onChange={(e) => handleInputChange('amount', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <footer className={styles['drawer-footer']}>
          <button className={[styles['primary-footer-btn'], styles['cancel-btn']].join(' ')} onClick={onClose}>
            Cancel
          </button>
          <button
            className={[styles['primary-footer-btn'], styles['mark-paid-btn']].join(' ')}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? <Loader2 size={18} className={styles['spinner']} /> : <CheckCircle2 size={18} />}
            <span>{isSubmitting ? (payment ? 'Updating...' : 'Recording...') : (payment ? 'Update Payment' : 'Mark as Paid')}</span>
          </button>
        </footer>
      </div>
    </div>
  );
};

export default AddVendorPaymentDrawer;
